import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { adminPageTitles } from '../shared/adminPageNames';
import AddEditModal from './addEditModal.component';
import DeleteModal from './deleteModal.component';
import AdminImportTab from '../clientAdministrationPage/shared/adminImportTab.component';
import { selectionCheckboxColumnFactory, UseSelectableRowGridLogicApi } from '@client-shared/agGrid/selectableRows';
import { YesNoFlag } from '../../shared/types';
import { columnDefinitions } from '../../shared/columnDefinitions/clientPage';
import { ClientPageAdminColumnRendererFactory } from './adminColumnRenderer.component';
import useAddEditModalControls from '../clientAdministrationPage/shared/useAddEditModalControls';
import useRemoveModalControls from '../clientAdministrationPage/shared/useRemoveModalControls';
import { useClientList } from '../../shared/queries/client';
import { useAddClient, useRemoveClient, useUpdateClient } from '../../shared/mutations/client';
import { Client, FetchedClient } from './types';
import { fetchUserClientList } from '../../shared/store/actions';

type ClientsGridSectionProps = {
  canModify: boolean,
  selectionApi: UseSelectableRowGridLogicApi<Client>,
  featureFlags: Record<string, any>,
  onGridReady: () => void,
  canUserModifyMultipleClients: boolean,

  environmentSuffix: string,
  shouldDisplayZero: boolean,
  hideDecimals: boolean,
  negativeMinus: boolean,
}

export const ClientsGridSection = ({
  canModify,
  selectionApi,
  featureFlags,
  onGridReady,
  canUserModifyMultipleClients,
  environmentSuffix,
  shouldDisplayZero,
  hideDecimals,
  negativeMinus,
}: ClientsGridSectionProps) => {
  const dispatch = useDispatch();

  const {
    hideModalForAddEdit,
    showModalForAdd,
    showModalForEdit,
    entityToEdit,
    isAddEditModalVisible,
  } = useAddEditModalControls<Client>();

  const {
    hideModalForRemove,
    showModalForRemove,
    entityToRemove,
    rowToRemoveIndex,
    isRemoveModalVisible,
  } = useRemoveModalControls<Client>();

  const {
    isFetching: isFetchingEntities,
    data: entities,
    refetch: fetchEntities,
  } = useClientList();

  const mappedEntities = useMemo(() => entities.map(
    (client: FetchedClient) => ({
      ...client,
      ...Object.keys(client.featureFlags)
        .reduce((allFeatureFlag, featureFlag) => ({
          ...allFeatureFlag,
          [featureFlag]: client.featureFlags[featureFlag].value,
        }), {}),
    })
  ), [entities]);

  const { mutate: addEntity, isLoading: isAddingEntity } = useAddClient();
  const { mutate: updateEntity, isLoading: isUpdatingEntity } = useUpdateClient();
  const { mutate: removeEntityMutation, isLoading: isRemovingEntity } = useRemoveClient(() => {
    hideModalForRemove();
  });

  const onSaveSuccess = useCallback((formData, { keepOpenAfterSaving, callback }) => {
    if (!keepOpenAfterSaving) {
      hideModalForAddEdit();
    }

    dispatch(fetchUserClientList() as any);

    if (callback) {
      callback();
    }
  }, [hideModalForAddEdit, dispatch]);

  const removeEntity = useCallback(
    async formData => {
      removeEntityMutation(formData);
    }, [removeEntityMutation]
  );

  const saveEntity = useCallback(
    async (formData, openForEdit, options) => {
      if (openForEdit) {
        const clientData = { ...formData, clientId: options?.params?.clientId };
        updateEntity(
          clientData,
          { onSuccess: () => onSaveSuccess(formData, options) }
        );
      } else {
        addEntity(
          formData,
          { onSuccess: () => onSaveSuccess(formData, options) }
        );
      }
    }, [addEntity, updateEntity, onSaveSuccess]
  );

  const isSavingEntity = isAddingEntity || isUpdatingEntity;

  const featureFlagsArray = useMemo(() => Object.values(featureFlags), [featureFlags]);

  const extendedColumnDefs = useMemo(() => [
    ...(canUserModifyMultipleClients ? [
      selectionCheckboxColumnFactory<Client>({
        selectionApi,
        isDisabled: ({ data }) => data.enabledFlag !== YesNoFlag.Y,
        getTooltipText: ({ data }) => data.enabledFlag !== YesNoFlag.Y
          ? 'Cannot select disabled clients'
          : null,
      }),
    ] : []),
    ...columnDefinitions(featureFlagsArray),
  ], [canUserModifyMultipleClients, selectionApi, featureFlagsArray]);

  const adminColumnRenderer = useMemo(() => ClientPageAdminColumnRendererFactory({
    showModalForEdit,
    showModalForRemove,
  }), [
    showModalForEdit,
    showModalForRemove,
  ]);
  return (
    <AdminImportTab
      pageTitle={adminPageTitles.CLIENTS}
      canModify={canModify}
      AddEditEntityModal={AddEditModal}
      RemoveEntityModal={DeleteModal}
      adminColumnRenderer={adminColumnRenderer}
      addEntityMessage="Add Client"
      columnDefs={extendedColumnDefs}
      comparatorConfig={{ idField: 'clientId' }}
      hasPeriodControl={false}

      environmentSuffix={environmentSuffix}
      shouldDisplayZero={shouldDisplayZero}
      hideDecimals={hideDecimals}
      negativeMinus={negativeMinus}

      saveEntity={saveEntity}
      isSavingEntity={isSavingEntity}

      removeEntity={removeEntity}
      isRemovingEntity={isRemovingEntity}

      isFetchingEntities={isFetchingEntities}
      fetchEntities={fetchEntities}
      entities={mappedEntities}

      isAddEditModalVisible={isAddEditModalVisible}
      entityToEdit={entityToEdit}

      isRemoveModalVisible={isRemoveModalVisible}
      entityToRemove={entityToRemove}
      rowToRemoveIndex={rowToRemoveIndex}

      showModalForAdd={showModalForAdd}
      hideModalForAddEdit={hideModalForAddEdit}
      hideModalForRemove={hideModalForRemove}

      onGridReady={onGridReady}
    />
  );
};
